import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import {
  CLASSIFICATION_OPTIONS,
  classificationLabel,
  type ExpenseAllocation,
  type ExpenseClassification,
  type OverheadReviewAccount,
  type OverheadReviewAmount,
} from "../lib/overhead-review.js";

interface Props {
  account: OverheadReviewAccount;
  currency: string;
  canEdit: boolean;
  compact: boolean;
  saving: boolean;
  onUpdateAccount: (input: {
    accountId: string;
    classification: ExpenseClassification | null;
    planningMonthlyAmount?: number | null;
  }) => Promise<void>;
  onSaveSplit: (input: { amountId: string; allocations: ExpenseAllocation[] }) => Promise<boolean>;
}

export function OverheadAccountReview({
  account,
  currency,
  canEdit,
  compact,
  saving,
  onUpdateAccount,
  onSaveSplit,
}: Props): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [planningDraft, setPlanningDraft] = useState(formatMoneyInput(account.planningAmountToUse));

  useEffect(() => {
    setPlanningDraft(formatMoneyInput(account.planningAmountToUse));
  }, [account.planningAmountToUse]);

  const planningValue = parseMoneyInput(planningDraft);
  const planningDirty = Number.isFinite(planningValue)
    && Math.abs(planningValue - account.planningAmountToUse) > 0.005;
  const answerOptions = suggestedFirst(account.suggestedClassification);

  return (
    <section style={styles.row(account.confirmedClassification === null)}>
      <div style={{
        display: "grid",
        gridTemplateColumns: compact ? "1fr" : "minmax(190px, 1.15fr) minmax(170px, .9fr) minmax(220px, 1.15fr) minmax(220px, 1fr)",
        gap: compact ? 14 : 18,
        alignItems: "start",
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 750, color: "var(--text)" }}>{account.name}</div>
          <div style={styles.meta}>{account.path}</div>
          <div className="dd-num" style={{ marginTop: 7, fontSize: 13, fontWeight: 650 }}>
            {money(account.totalAmount, currency)} total
          </div>
          <div style={styles.meta}>Found in {account.monthsSeen} of {account.reportMonthCount} months</div>
        </div>

        <div>
          <div style={styles.label}>Our suggestion</div>
          <div style={{ fontSize: 13, fontWeight: 650, marginTop: 4 }}>
            {classificationLabel(account.suggestedClassification)}
          </div>
          <div style={{ ...styles.meta, marginTop: 5, lineHeight: 1.45 }}>{account.suggestionReason}</div>
        </div>

        <div>
          <label htmlFor={`expense-answer-${account.id}`} style={styles.label}>Your answer</label>
          <select
            id={`expense-answer-${account.id}`}
            value={account.confirmedClassification ?? ""}
            disabled={!canEdit || saving}
            onChange={(event) => {
              const value = event.target.value as ExpenseClassification | "";
              void onUpdateAccount({
                accountId: account.id,
                classification: value || null,
              });
            }}
            style={styles.select}
          >
            <option value="">Choose one</option>
            {answerOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}{option.value === account.suggestedClassification ? " (suggested)" : ""}
              </option>
            ))}
          </select>
          <div style={{ ...styles.meta, marginTop: 5 }}>
            You can change this later. QuickBooks keeps the original amount.
          </div>
        </div>

        <div>
          {account.confirmedClassification === "recurring_overhead" ? (
            <>
              <label htmlFor={`expense-plan-${account.id}`} style={styles.label}>Use each month</label>
              <div style={{ display: "flex", gap: 7, alignItems: "center", marginTop: 4 }}>
                <div style={{ position: "relative", flex: 1 }}>
                  <span aria-hidden="true" style={styles.currencyPrefix}>$</span>
                  <input
                    id={`expense-plan-${account.id}`}
                    type="text"
                    inputMode="decimal"
                    disabled={!canEdit || saving}
                    value={planningDraft}
                    onChange={(event) => setPlanningDraft(event.target.value)}
                    onBlur={() => {
                      if (Number.isFinite(planningValue) && planningValue >= 0) setPlanningDraft(formatMoneyInput(planningValue));
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && planningDirty && planningValue >= 0) {
                        event.preventDefault();
                        void onUpdateAccount({
                          accountId: account.id,
                          classification: "recurring_overhead",
                          planningMonthlyAmount: planningValue,
                        });
                      }
                    }}
                    style={{ ...styles.input, paddingLeft: 22 }}
                  />
                </div>
                {planningDirty && planningValue >= 0 && (
                  <button
                    type="button"
                    disabled={!canEdit || saving}
                    onClick={() => void onUpdateAccount({
                      accountId: account.id,
                      classification: "recurring_overhead",
                      planningMonthlyAmount: planningValue,
                    })}
                    style={styles.smallPrimary}
                  >
                    Save
                  </button>
                )}
              </div>
              <div style={{ ...styles.meta, marginTop: 5 }}>
                Suggested: {money(account.suggestedPlanningMonthlyAmount, currency)} per month. Change it if the next few months will be different.
              </div>
            </>
          ) : (
            <div style={{ ...styles.meta, paddingTop: 18 }}>
              {account.confirmedClassification === null
                ? "Choose an answer to finish this account."
                : "This will not be added to normal monthly overhead."}
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        style={styles.monthToggle}
      >
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        View monthly amounts and split an unusual month
      </button>

      {open && (
        <div style={styles.monthList}>
          <div style={{ fontSize: 12, color: "var(--body)", lineHeight: 1.5 }}>
            These amounts came from QuickBooks and cannot be edited here. If one is wrong, fix it in QuickBooks and refresh. You can split a month without changing the source amount.
          </div>
          {account.amounts.map((amount) => (
            <MonthAmountRow
              key={amount.id}
              amount={amount}
              currency={currency}
              canEdit={canEdit}
              saving={saving}
              onSave={(allocations) => onSaveSplit({ amountId: amount.id, allocations })}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function MonthAmountRow({
  amount,
  currency,
  canEdit,
  saving,
  onSave,
}: {
  amount: OverheadReviewAmount;
  currency: string;
  canEdit: boolean;
  saving: boolean;
  onSave: (allocations: ExpenseAllocation[]) => Promise<boolean>;
}): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const initialAllocations = amount.allocations.length > 0
    ? amount.allocations
    : [
        { classification: "recurring_overhead" as const, amount: amount.amount },
        { classification: "one_time_operating" as const, amount: 0 },
      ];
  const [draft, setDraft] = useState<ExpenseAllocation[]>(initialAllocations);

  useEffect(() => {
    setDraft(amount.allocations.length > 0
      ? amount.allocations
      : [
          { classification: "recurring_overhead", amount: amount.amount },
          { classification: "one_time_operating", amount: 0 },
        ]);
  }, [amount.id, amount.amount, amount.allocations]);

  const total = draft.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const difference = Math.round((amount.amount - total) * 100) / 100;
  const matches = Math.abs(difference) <= 0.01;

  return (
    <div style={styles.monthRow}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <span style={{ fontSize: 13, fontWeight: 650 }}>{monthLabel(amount.month)}</span>
          <span className="dd-num" style={{ marginLeft: 10, fontSize: 13 }}>{money(amount.amount, currency)}</span>
        </div>
        <button
          type="button"
          disabled={!canEdit || saving}
          onClick={() => setEditing((value) => !value)}
          style={styles.textButton}
        >
          {editing ? "Close" : amount.allocations.length > 0 ? "Edit split" : "Split this month"}
        </button>
      </div>

      {amount.allocations.length > 0 && !editing && (
        <div style={{ ...styles.meta, marginTop: 5 }}>
          {amount.allocations.map((allocation) => `${classificationLabel(allocation.classification)}: ${money(allocation.amount, currency)}`).join(" | ")}
        </div>
      )}

      {editing && (
        <div style={styles.splitEditor}>
          {draft.map((row, index) => (
            <div key={`${row.classification}-${index}`} style={{ display: "grid", gridTemplateColumns: "minmax(190px, 1fr) minmax(120px, .55fr) auto", gap: 8, alignItems: "center" }}>
              <select
                aria-label={`Type for split part ${index + 1}`}
                value={row.classification}
                onChange={(event) => setDraft((current) => current.map((item, itemIndex) => itemIndex === index
                  ? { ...item, classification: event.target.value as ExpenseClassification }
                  : item))}
                style={styles.select}
              >
                {CLASSIFICATION_OPTIONS.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={draft.some((item, itemIndex) => itemIndex !== index && item.classification === option.value)}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                aria-label={`Amount for split part ${index + 1}`}
                value={row.amount}
                onChange={(event) => setDraft((current) => current.map((item, itemIndex) => itemIndex === index
                  ? { ...item, amount: Number(event.target.value) }
                  : item))}
                style={styles.input}
              />
              <button
                type="button"
                aria-label={`Remove split part ${index + 1}`}
                disabled={draft.length <= 1}
                onClick={() => setDraft((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                style={styles.iconButton}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}

          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              disabled={draft.length >= CLASSIFICATION_OPTIONS.length}
              onClick={() => {
                const next = CLASSIFICATION_OPTIONS.find((option) => !draft.some((row) => row.classification === option.value));
                if (next) setDraft((current) => [...current, { classification: next.value, amount: 0 }]);
              }}
              style={styles.textButton}
            >
              <Plus size={14} /> Add another part
            </button>
            <div className="dd-num" style={{ fontSize: 12, color: matches ? "var(--good)" : "var(--bad)" }}>
              {matches ? "Matches the QuickBooks amount" : `${money(Math.abs(difference), currency)} ${difference > 0 ? "still needs a type" : "over the source amount"}`}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
            {amount.allocations.length > 0 && (
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  if (await onSave([])) setEditing(false);
                }}
                style={styles.secondaryButton}
              >
                Remove split
              </button>
            )}
            <button
              type="button"
              disabled={saving || !matches || draft.length === 0}
              onClick={async () => {
                if (await onSave(draft)) setEditing(false);
              }}
              style={styles.smallPrimary}
            >
              Save split
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function money(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

/** A text input is intentional: native number inputs reject grouping commas. */
export function parseMoneyInput(value: string): number {
  const cleaned = value.replace(/[$,\s]/g, "");
  return cleaned ? Number(cleaned) : Number.NaN;
}

export function formatMoneyInput(value: number): string {
  if (!Number.isFinite(value)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
}

export function suggestedFirst(suggested: ExpenseClassification | null): typeof CLASSIFICATION_OPTIONS {
  if (suggested === null) return CLASSIFICATION_OPTIONS;
  return [...CLASSIFICATION_OPTIONS].sort((a, b) => Number(b.value === suggested) - Number(a.value === suggested));
}

function monthLabel(value: string): string {
  return new Date(`${value.slice(0, 7)}-15T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

const styles = {
  row: (needsReview: boolean): React.CSSProperties => ({
    padding: 16,
    border: `1px solid ${needsReview ? "color-mix(in srgb, var(--bad) 42%, var(--border))" : "var(--border)"}`,
    borderRadius: 10,
    background: "var(--surface)",
  }),
  label: {
    display: "block",
    color: "var(--faint)",
    fontSize: 11.5,
    fontWeight: 650,
  } as React.CSSProperties,
  meta: {
    color: "var(--faint)",
    fontSize: 11.5,
    lineHeight: 1.35,
  } as React.CSSProperties,
  select: {
    width: "100%",
    marginTop: 4,
    padding: "8px 9px",
    background: "var(--surface-2)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 7,
    font: "inherit",
    fontSize: 12.5,
  } as React.CSSProperties,
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 9px",
    background: "var(--surface-2)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 7,
    font: "inherit",
    fontSize: 12.5,
  } as React.CSSProperties,
  currencyPrefix: {
    position: "absolute",
    left: 9,
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--faint)",
    fontSize: 12,
    pointerEvents: "none",
  } as React.CSSProperties,
  monthToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginTop: 12,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "var(--accent)",
    cursor: "pointer",
    font: "inherit",
    fontSize: 12,
    fontWeight: 650,
  } as React.CSSProperties,
  monthList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px solid var(--border)",
  } as React.CSSProperties,
  monthRow: {
    padding: "10px 12px",
    borderRadius: 8,
    background: "var(--surface-2)",
  } as React.CSSProperties,
  splitEditor: {
    display: "flex",
    flexDirection: "column",
    gap: 9,
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1px solid var(--border)",
  } as React.CSSProperties,
  textButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    border: "none",
    background: "transparent",
    color: "var(--accent)",
    cursor: "pointer",
    padding: 0,
    font: "inherit",
    fontSize: 12,
    fontWeight: 650,
  } as React.CSSProperties,
  smallPrimary: {
    border: "none",
    borderRadius: 7,
    padding: "8px 11px",
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    cursor: "pointer",
    font: "inherit",
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  } as React.CSSProperties,
  secondaryButton: {
    border: "1px solid var(--border)",
    borderRadius: 7,
    padding: "7px 10px",
    background: "transparent",
    color: "var(--body)",
    cursor: "pointer",
    font: "inherit",
    fontSize: 12,
    fontWeight: 650,
  } as React.CSSProperties,
  iconButton: {
    width: 31,
    height: 31,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid var(--border)",
    borderRadius: 7,
    background: "transparent",
    color: "var(--bad)",
    cursor: "pointer",
  } as React.CSSProperties,
};
